import React, { useState, useEffect, useMemo } from "react";
import { materialService } from "../../services/materialService";
import { useMaterials } from "../../query/hooks";
import {
  PlusIcon,
  PencilIcon,
  TrashIcon,
  EyeIcon,
  ExclamationTriangleIcon,
  CheckCircleIcon,
} from "@heroicons/react/24/outline";
import { uploadImageToCloudinary } from "../../upload-image-config";
import LogoLoader from "../Common/LogoLoader";

const MaterialAdmin = () => {
  const materialsQuery = useMaterials();
  const materials = useMemo(() => materialsQuery.data ?? [], [materialsQuery.data]);
  const loading = materialsQuery.isLoading;
  const [isPending, setIsPending] = useState(false);
  const [message, setMessage] = useState("");
  const [showForm, setShowForm] = useState(false);
  const [selectedImage, setSelectedImage] = useState(null);
  const [pendingSampleFiles, setPendingSampleFiles] = useState([]);
  const [pendingSamplePreviews, setPendingSamplePreviews] = useState([]);
  const [editingMaterial, setEditingMaterial] = useState(null);
  const [formData, setFormData] = useState({
    name: "",
    category: "",
    description: "",
    pricePerKg: "",
    image: "",
    sampleImages: [],
  });
  const [materialsPage, setMaterialsPage] = useState(1);
  const MATERIALS_PAGE_SIZE = 20;

  const pagedMaterials = useMemo(() => {
    const start = (materialsPage - 1) * MATERIALS_PAGE_SIZE;
    return materials.slice(start, start + MATERIALS_PAGE_SIZE);
  }, [materials, materialsPage]);

  const materialsTotalPages = Math.max(1, Math.ceil(materials.length / MATERIALS_PAGE_SIZE));

  useEffect(() => {
    setMaterialsPage(1);
  }, [materials.length]);

  useEffect(() => {
    if (materialsQuery.isError) setMessage("Error loading materials");
  }, [materialsQuery.isError]);

  const fetchMaterials = () => materialsQuery.refetch();

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  const handleImageChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      setSelectedImage(URL.createObjectURL(file));
      setFormData((prev) => ({
        ...prev,
        image: file,
      }));
    }
  };

  const handleSampleImagesChange = (e) => {
    const files = Array.from(e.target.files || []);
    if (files.length === 0) return;

    const previews = files.map((f) => URL.createObjectURL(f));
    setPendingSampleFiles((prev) => [...prev, ...files]);
    setPendingSamplePreviews((prev) => [...prev, ...previews]);

    // Allow re-selecting the same file again if needed
    e.target.value = "";
  };

  const removeExistingSampleImage = (urlToRemove) => {
    setFormData((prev) => ({
      ...prev,
      sampleImages: (prev.sampleImages || []).filter((u) => u !== urlToRemove),
    }));
  };

  const removePendingSampleImage = (index) => {
    setPendingSampleFiles((prev) => prev.filter((_, i) => i !== index));
    setPendingSamplePreviews((prev) => prev.filter((_, i) => i !== index));
  };

  const resetForm = () => {
    setFormData({
      name: "",
      category: "",
      description: "",
      pricePerKg: "",
      image: "",
      sampleImages: [],
    });
    setEditingMaterial(null);
    setSelectedImage(null);
    setPendingSampleFiles([]);
    setPendingSamplePreviews([]);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setMessage("");

    try {
      setIsPending(true);
      let imageUrl = formData.image;

      if (formData.image instanceof File) {
        imageUrl = await uploadImageToCloudinary(formData.image);
      }

      let sampleImageUrls = Array.isArray(formData.sampleImages)
        ? [...formData.sampleImages]
        : [];
      if (pendingSampleFiles.length > 0) {
        const uploaded = await Promise.all(
          pendingSampleFiles.map((f) => uploadImageToCloudinary(f))
        );
        sampleImageUrls = [...sampleImageUrls, ...uploaded];
      }
      // de-dupe while keeping order
      sampleImageUrls = Array.from(new Set(sampleImageUrls.filter(Boolean)));

      const payload = {
        ...formData,
        image: imageUrl,
        sampleImages: sampleImageUrls,
      };
      if (editingMaterial) {
        await materialService.updateMaterial(editingMaterial._id, payload);
        setMessage("Material updated successfully!");
      } else {
        await materialService.createMaterial(payload);
        setMessage("Material created successfully!");
      }

      await fetchMaterials();
      setShowForm(false);
      resetForm();
    } catch (error) {
      console.error("Error saving material:", error);
      setMessage(error.response?.data?.message || "Error saving material");
    } finally {
      setIsPending(false);
    }
  };

  const handleEdit = (material) => {
    setEditingMaterial(material);
    setFormData({
      name: material.name,
      category: material.category,
      description: material.description,
      pricePerKg: material.pricePerKg.toString(),
      image: material.image || "",
      sampleImages: Array.isArray(material.sampleImages) ? material.sampleImages : [],
    });
    setSelectedImage(null);
    setPendingSampleFiles([]);
    setPendingSamplePreviews([]);
    setShowForm(true);
  };

  const handleDelete = async (materialId) => {
    if (!window.confirm("Are you sure you want to delete this material?")) {
      return;
    }

    try {
      await materialService.deleteMaterial(materialId);
      setMessage("Material deleted successfully!");
      await fetchMaterials();
    } catch (error) {
      console.error("Error deleting material:", error);
      setMessage(error.response?.data?.message || "Error deleting material");
    }
  };

  const categories = [
    {
      name: "Household Wastes",
      enum: "household_wastes",
    },
    {
      name: "Industrial Waste",
      enum: "industrial_waste",
    },
    {
      name: "Raw Materials",
      enum: "raw_materials",
    },
  ];

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <LogoLoader />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="bg-white rounded-lg shadow p-6">
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-2xl font-bold text-gray-900 mb-2">
              Material Management
            </h1>
            <p className="text-gray-600">
              Create, edit, and manage materials available for sale on the
              platform.
            </p>
          </div>
          <button
            onClick={() => {
              resetForm();
              setShowForm(true);
            }}
            className="bg-green-600 hover:bg-green-700 text-white px-4 py-2 rounded-md flex items-center"
          >
            <PlusIcon className="h-5 w-5 mr-2" />
            Add Material
          </button>
        </div>
      </div>

      {/* Message */}
      {message && (
        <div
          className={`p-4 rounded-md ${
            message.includes("successfully")
              ? "bg-green-100 text-green-700 border border-green-200"
              : "bg-red-100 text-red-700 border border-red-200"
          }`}
        >
          <div className="flex items-center">
            {message.includes("successfully") ? (
              <CheckCircleIcon className="h-5 w-5 mr-2" />
            ) : (
              <ExclamationTriangleIcon className="h-5 w-5 mr-2" />
            )}
            {message}
          </div>
        </div>
      )}

      {/* Material Form */}
      {showForm && (
        <div className="bg-white rounded-lg shadow p-6">
          <h2 className="text-lg font-semibold text-gray-900 mb-4">
            {editingMaterial ? "Edit Material" : "Add New Material"}
          </h2>

          <form onSubmit={handleSubmit} className="space-y-4">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Material Name *
                </label>
                <input
                  type="text"
                  name="name"
                  value={formData.name}
                  onChange={handleInputChange}
                  required
                  className="w-full border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-2 focus:ring-green-500"
                  placeholder="e.g., Plastic Bottles"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Category *
                </label>
                <select
                  name="category"
                  value={formData.category}
                  onChange={handleInputChange}
                  required
                  className="w-full border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-2 focus:ring-green-500"
                >
                  <option value="">Select Category</option>
                  {categories.map((category) => (
                    <option key={category.enum} value={category.enum}>
                      {category.name}
                    </option>
                  ))}
                </select>
              </div>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Description
              </label>
              <textarea
                name="description"
                value={formData.description}
                onChange={handleInputChange}
                rows="3"
                className="w-full border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-2 focus:ring-green-500"
                placeholder="Describe the material..."
              />
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Price per KG (₦) *
                </label>
                <input
                  type="number"
                  name="pricePerKg"
                  value={formData.pricePerKg}
                  onChange={handleInputChange}
                  required
                  min="0"
                  step="0.01"
                  className="w-full border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-2 focus:ring-green-500"
                  placeholder="0.00"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Main Image
                </label>
                <input
                  type="file"
                  accept="image/*"
                  onChange={handleImageChange}
                  className="w-full border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-2 focus:ring-green-500"
                  placeholder="https://example.com/image.jpg"
                />
              </div>
              {selectedImage && (
                <img
                  src={selectedImage}
                  alt="Selected preview"
                  className="mt-4 max-w-full h-auto rounded-md"
                />
              )}
            </div>

            {/* Sample images */}
            <div className="border-t pt-4">
              <div className="flex items-center justify-between gap-3 flex-wrap">
                <div>
                  <label className="block text-sm font-medium text-gray-700">
                    Sample Images
                  </label>
                  <p className="text-xs text-gray-500 mt-1">
                    Upload as many sample images as you want. These show on the material details page.
                  </p>
                </div>
                <div>
                  <input
                    type="file"
                    accept="image/*"
                    multiple
                    onChange={handleSampleImagesChange}
                    className="w-full border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-2 focus:ring-green-500"
                  />
                </div>
              </div>

              {(formData.sampleImages?.length > 0 || pendingSamplePreviews.length > 0) && (
                <div className="mt-4 grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-3">
                  {(formData.sampleImages || []).map((url) => (
                    <div key={url} className="relative group">
                      <img
                        src={url}
                        alt="Sample"
                        className="h-24 w-full object-cover rounded-md border"
                      />
                      <button
                        type="button"
                        onClick={() => removeExistingSampleImage(url)}
                        className="absolute top-2 right-2 rounded-md bg-white/90 border px-2 py-1 text-xs text-gray-700 opacity-0 group-hover:opacity-100 transition"
                        title="Remove"
                      >
                        Remove
                      </button>
                    </div>
                  ))}
                  {pendingSamplePreviews.map((preview, idx) => (
                    <div key={`${preview}-${idx}`} className="relative group">
                      <img
                        src={preview}
                        alt="Sample (pending)"
                        className="h-24 w-full object-cover rounded-md border border-dashed"
                      />
                      <button
                        type="button"
                        onClick={() => removePendingSampleImage(idx)}
                        className="absolute top-2 right-2 rounded-md bg-white/90 border px-2 py-1 text-xs text-gray-700 opacity-0 group-hover:opacity-100 transition"
                        title="Remove"
                      >
                        Remove
                      </button>
                      <div className="absolute bottom-2 left-2 rounded bg-black/60 px-2 py-0.5 text-[11px] text-white">
                        Pending upload
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>

            <div className="flex justify-end space-x-3">
              <button
                type="button"
                onClick={() => {
                  setShowForm(false);
                  resetForm();
                }}
                className="px-4 py-2 border border-gray-300 rounded-md text-gray-700 hover:bg-gray-50"
              >
                Cancel
              </button>
              <button
                disabled={isPending}
                type="submit"
                className="px-4 py-2 bg-green-600 hover:bg-green-700 text-white rounded-md"
              >
                {editingMaterial ? "Update Material" : "Create Material"}
              </button>
            </div>
          </form>
        </div>
      )}

      {/* Materials List */}
      <div className="bg-white rounded-lg shadow">
        <div className="px-6 py-4 border-b border-gray-200">
          <h2 className="text-lg font-semibold text-gray-900">
            Materials ({materials.length})
          </h2>
        </div>

        {materials.length === 0 ? (
          <div className="p-12 text-center">
            <div className="text-gray-400 mb-4">
              <PlusIcon className="h-16 w-16 mx-auto" />
            </div>
            <h3 className="text-lg font-medium text-gray-900 mb-2">
              No materials yet
            </h3>
            <p className="text-gray-600 mb-4">
              Start by adding your first material to the platform.
            </p>
            <button
              onClick={() => {
                resetForm();
                setShowForm(true);
              }}
              className="bg-green-600 hover:bg-green-700 text-white px-4 py-2 rounded-md"
            >
              Add First Material
            </button>
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Material
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Category
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Price/KG
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Status
                  </th>
                  <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Actions
                  </th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {pagedMaterials.map((material) => (
                  <tr key={material._id} className="hover:bg-gray-50">
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="flex items-center">
                        {material.image && (
                          <img
                            className="h-10 w-10 rounded-full object-cover mr-3"
                            src={material.image}
                            alt={material.name}
                          />
                        )}
                        <div>
                          <div className="text-sm font-medium text-gray-900">
                            {material.name}
                          </div>
                          {material.description && (
                            <div className="text-sm text-gray-500 truncate max-w-xs">
                              {material.description}
                            </div>
                          )}
                        </div>
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <span className="inline-flex px-2 py-1 text-xs font-semibold rounded-full bg-blue-100 text-blue-800">
                        {material.category}
                      </span>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                      ₦{material.pricePerKg?.toFixed(2) || "0.00"}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <span className="inline-flex px-2 py-1 text-xs font-semibold rounded-full bg-green-100 text-green-800">
                        Active
                      </span>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                      <div className="flex items-center justify-end space-x-2">
                        <button
                          onClick={() => handleEdit(material)}
                          className="text-blue-600 hover:text-blue-900"
                        >
                          <PencilIcon className="h-4 w-4" />
                        </button>
                        <button
                          onClick={() => handleDelete(material._id)}
                          className="text-red-600 hover:text-red-900"
                        >
                          <TrashIcon className="h-4 w-4" />
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
            {materials.length > MATERIALS_PAGE_SIZE && (
              <div className="flex items-center justify-between px-6 py-3 border-t border-gray-200 bg-gray-50">
                <p className="text-sm text-gray-600">
                  Showing {(materialsPage - 1) * MATERIALS_PAGE_SIZE + 1}–
                  {Math.min(materialsPage * MATERIALS_PAGE_SIZE, materials.length)} of {materials.length}
                </p>
                <div className="flex gap-2">
                  <button
                    type="button"
                    disabled={materialsPage <= 1}
                    onClick={() => setMaterialsPage((p) => Math.max(1, p - 1))}
                    className="px-3 py-1.5 text-sm border rounded-md disabled:opacity-40"
                  >
                    Previous
                  </button>
                  <button
                    type="button"
                    disabled={materialsPage >= materialsTotalPages}
                    onClick={() => setMaterialsPage((p) => Math.min(materialsTotalPages, p + 1))}
                    className="px-3 py-1.5 text-sm border rounded-md disabled:opacity-40"
                  >
                    Next
                  </button>
                </div>
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
};

export default MaterialAdmin;
